/**
 * Main application component for the Musedrops admin interface.
 *
 * This is the root component that sets up the React-Admin application with:
 * - Supabase data provider for backend integration
 * - Material-UI theme configuration
 * - Resource definitions for Shows and Stories
 * - Custom routes for configuration and debugging
 *
 * @fileoverview Main app entry point with React-Admin configuration
 */

import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route, BrowserRouter } from 'react-router-dom';
import {
  Radio,
  BookOpen,
  BarChart3,
  Settings,
  FlaskConical,
  Blocks,
  Users,
  SwatchBook,
  Activity as ActivityIcon,
} from 'lucide-react';

import { ShowList } from '@features/shows/pages/ShowList';
import { ShowEdit } from '@features/shows/pages/ShowEdit';
import { ShowCreate } from '@features/shows/pages/ShowCreate';
import { StoryList } from '@features/stories/pages/StoryList';
import { StoryCreateWrapper } from '@features/stories/pages/StoryCreate';
import { StoryEdit } from '@features/stories/pages/StoryEdit';
// Issue #640: StoryDetailsPage removed - no longer in use
import { ProcessLogList } from '@features/monitoring/pages/Monitoring';
import { Settings as SettingsPage } from '@features/settings/pages/Settings';
import { PromptDebugConsole } from '@features/prompt-debugger/pages/PromptDebugger';
import { BuildingBlocks } from '@features/building-blocks';
import { UserList } from '@features/users/pages/UserList';
import { UserDetail } from '@features/users/pages/UserDetail';
import { RecSys as RecSysPage } from '@features/rec-sys/pages/RecSys';
import { Activity as ActivityPage } from '@features/activity/pages/Activity';
import { LoginPage } from '@features/auth/pages/LoginPage';
import { AppLayout } from '@features/auth/components/AppLayout';
import { dataProvider } from './services/supabase';
import { authProvider } from './services/authProvider';
import { getBypassAuthProvider } from './utils/authBypass';
import { lightTheme, darkTheme } from './theme';

function App() {
  // Check for auth bypass in development/testing (#708: DEV guard lets
  // production builds dead-code-eliminate the bypass path)
  const bypassAuthProvider = import.meta.env.DEV ? getBypassAuthProvider() : null;
  const activeAuthProvider = bypassAuthProvider || authProvider;

  return (
    <BrowserRouter>
      <Admin
        dataProvider={dataProvider}
        authProvider={activeAuthProvider}
        loginPage={LoginPage}
        layout={AppLayout}
        theme={lightTheme}
        darkTheme={darkTheme}
        requireAuth
      >
        <Resource
          name="shows"
          list={ShowList}
          create={ShowCreate}
          edit={ShowEdit}
          icon={Radio}
          recordRepresentation="name"
        />
        <Resource
          name="stories"
          list={StoryList}
          edit={StoryEdit}
          icon={BookOpen}
          recordRepresentation="title"
        />
        <Resource
          name="monitoring"
          list={ProcessLogList}
          icon={BarChart3}
          options={{ label: 'Monitoring' }}
        />
        <Resource
          name="prompt-debugger"
          list={PromptDebugConsole}
          icon={FlaskConical}
          options={{ label: 'Prompt Debugger' }}
        />
        <Resource
          name="building-blocks"
          list={BuildingBlocks}
          icon={Blocks}
          options={{ label: 'Building Blocks' }}
        />
        <Resource
          name="rec-sys"
          list={RecSysPage}
          icon={SwatchBook}
          options={{ label: 'Rec Sys' }}
        />
        <Resource
          name="settings"
          list={SettingsPage}
          icon={Settings}
          options={{ label: 'Settings' }}
        />
        <Resource
          name="users"
          list={UserList}
          icon={Users}
          options={{ label: 'Users' }}
        />
        <Resource
          name="activity"
          list={ActivityPage}
          icon={ActivityIcon}
          options={{ label: 'Activity' }}
        />
        <Resource name="user_listened_stories" />
        <Resource name="exposures" />
        <Resource name="drop_in_sessions" />
        <CustomRoutes>
          {/* Routes NOT generated by <Resource>: stories has no create page
              registered on the Resource, and users has no edit/show — the
              Resource list route ("/*") would otherwise swallow /users/:id.
              Other former entries duplicated Resource-generated routes
              (list at "/*", edit at ":id/*") and were removed (#748 / I-26). */}
          <Route path="/stories/create" element={<StoryCreateWrapper />} />
          {/* Issue #640: /stories/:id route removed - StoryDetailsPage no longer in use */}
          <Route path="/users/:id" element={<UserDetail />} />
        </CustomRoutes>
      </Admin>
    </BrowserRouter>
  );
}

export default App;
